import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import Swal from 'sweetalert2';
import * as dayjs from 'dayjs';
import { ConsultaPublicaService, ConsultaIEResponse } from 'src/app/services/consulta-publica.service';

export interface DadosIEFormatado {
  inscricaoEstadual: string;
  cpfCnpj: string;
  razaoSocial: string;
  categoriaOcupacao: string;
  situacaoIE: string;
  dataSituacaoIE: string;
  situacaoCpfCnpj: string;
  dataSituacaoCpfCnpj: string;
  cnaes: Array<{
    codigo: string;
    descricao: string;
    primario: boolean;
  }>;
  endereco: {
    logradouro: string;
    numero: string;
    complemento: string;
    cep: string;
    bairro: string;
    municipio: string;
    estado: string;
  };
}

@Component({
  selector: 'app-consulta-publica',
  templateUrl: './consulta-publica.component.html',
  styleUrls: ['./consulta-publica.component.scss']
})
export class ConsultaPublicaComponent implements OnInit {

  consultaForm: FormGroup;
  loading: boolean = false;
  dadosIE: DadosIEFormatado | null = null;
  listaIe: Array<{
    inscricaoEstadual: string;
    razaoSocial: string;
  }> = [];
  mostrarResultado: boolean = false;
  dataHoraConsulta: string = '';
  inscricaoSelecionadaIndex: number | null = null;
  mostrarMensagemValidacao: boolean = false;

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private consultaPublicaService: ConsultaPublicaService
  ) {
    this.consultaForm = this.fb.group({
      cpfCnpj: ['', [
        Validators.required,
        this.validarCpfCnpj.bind(this)
      ]]
    });
  }

  validarCpfCnpj(control: any): { [key: string]: any } | null {
    if (!control.value) {
      return null;
    }
    
    const apenasNumeros = control.value.replace(/\D/g, '');
    
    if (apenasNumeros.length === 11 || apenasNumeros.length === 14) {
      return null;
    }
    
    return { pattern: true };
  }

  ngOnInit(): void {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  get cpfCnpj() {
    return this.consultaForm.get('cpfCnpj');
  }

  apenasNumeros(event: KeyboardEvent): boolean {
    const charCode = event.which ? event.which : event.keyCode;
    // Permite: backspace, delete, tab, escape, enter
    if ([46, 8, 9, 27, 13].indexOf(charCode) !== -1 ||
      // Permite: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
      (charCode === 65 && event.ctrlKey === true) ||
      (charCode === 67 && event.ctrlKey === true) ||
      (charCode === 86 && event.ctrlKey === true) ||
      (charCode === 88 && event.ctrlKey === true) ||
      // Permite: pontos e traços para formatação
      charCode === 45 || charCode === 46 || charCode === 47) {
      return true;
    }
    // Permite apenas números
    if (charCode < 48 || charCode > 57) {
      event.preventDefault();
      return false;
    }
    return true;
  }

  validarFormulario(): boolean {
    if (this.consultaForm.invalid) {
      this.consultaForm.markAllAsTouched();
      
      let mensagemErro = 'Por favor, corrija os erros no formulário.';
      
      if (this.cpfCnpj?.errors?.['required']) {
        mensagemErro = 'Por favor, informe o CPF ou CNPJ.';
      } else if (this.cpfCnpj?.errors?.['pattern']) {
        mensagemErro = 'CPF ou CNPJ inválido. Informe 11 dígitos para CPF ou 14 para CNPJ.';
      }
      
      Swal.fire({
        icon: 'warning',
        title: 'Atenção',
        text: mensagemErro
      });
      return false;
    }

    return true;
  }

  consultar() {
    if (!this.validarFormulario()) {
      return;
    }

    Swal.fire({
      title: 'Aguarde',
      text: 'Validando as informações...',
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => {
        Swal.showLoading();
      }
    });

    const cpfCnpjValue = this.consultaForm.get('cpfCnpj')?.value;
    const cpfCnpjLimpo = cpfCnpjValue.replace(/\D/g, '');

    this.consultaPublicaService.consultarListaIe(cpfCnpjLimpo).subscribe(
      (res) => {
        Swal.close();
        if(res.length === 0) {
          Swal.fire({
            text: 'Nenhuma inscrição estadual encontrada para o CPF/CNPJ informado.',
            icon: 'info',
            confirmButtonText: 'OK'
          });

          return
        } 

        this.listaIe = res.map(item => ({
          razaoSocial: item.dscRazaoSocial,
          inscricaoEstadual: item.dscIe
        }));

        if(this.listaIe.length === 1) {
          this.inscricaoSelecionadaIndex = 0;
          this.prosseguir();
          return;
        }
        
        this.mostrarResultado = true;

      }, error => { 
        Swal.fire({
          icon: 'error',
          text: 'Ocorreu um erro ao consultar as inscrições estaduais. Por favor, tente novamente mais tarde.',
        })
      }
    )
  }

  selecionarInscricao(index: number): void {
    this.inscricaoSelecionadaIndex = index;
    this.mostrarMensagemValidacao = false;
  }

  prosseguir(): void {
    if (this.inscricaoSelecionadaIndex === null) {
      Swal.fire({
        icon: 'warning',
        text: "Selecione uma Inscrição Estadual para prosseguir."
      });
      return;
    }

    const inscricaoSelecionada = this.listaIe[this.inscricaoSelecionadaIndex];
    
    Swal.fire({
      title: 'Aguarde',
      text: 'Buscando informações da Inscrição Estadual...',
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => {
        Swal.showLoading();
      }
    });

    // Removendo formatação da IE antes de enviar
    const ieLimpa = inscricaoSelecionada.inscricaoEstadual.replace(/\D/g, '');
    
    this.consultaPublicaService.consultarPorIE(ieLimpa).subscribe(
      (response: ConsultaIEResponse) => {
        this.loading = false;
        Swal.close();
        this.dadosIE = this.formatarDadosIE(response);
        this.mostrarResultado = true;
        this.listaIe = []; // Limpa a lista para esconder a tabela
        this.dataHoraConsulta = (dayjs as any)().format('DD/MM/YYYY HH:mm:ss');
        window.scrollTo({ top: 0, behavior: 'smooth' });
      },
      (err) => {
        this.loading = false;
        Swal.close();
        
        if (err.status === 404) {
          Swal.fire({
            icon: 'error',
            title: 'Inscrição não encontrada',
            text: 'Não foi possível obter os detalhes da Inscrição Estadual selecionada.',
            confirmButtonText: 'OK'
          });
        } else {
          Swal.fire({
            icon: 'error',
            title: 'Erro na consulta',
            text: 'Não foi possível realizar a consulta. Por favor, tente novamente mais tarde.',
            confirmButtonText: 'OK'
          });
        }
      }
    );
  }

  handleNaoEncontrado() {
    Swal.fire({
      icon: 'error',
      title: 'CPF/CNPJ não encontrado',
      text: 'O CPF ou CNPJ informado não foi encontrado em nossa base de dados.',
      confirmButtonText: 'OK'
    }).then(() => {
      this.limparFormulario();
    });
  }

  handleErroConsulta() {
    Swal.fire({
      icon: 'error',
      title: 'Erro na consulta',
      text: 'Não foi possível realizar a consulta. Por favor, tente novamente mais tarde.',
      confirmButtonText: 'OK'
    });
  }

  limparFormulario() {
    this.consultaForm.reset();
    this.mostrarResultado = false;
    this.dadosIE = null;
    this.inscricaoSelecionadaIndex = null;
    this.mostrarMensagemValidacao = false;
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  novaConsulta() {
    this.limparFormulario();
  }

  imprimirPDF() {
    const dscIe = this.dadosIE?.inscricaoEstadual;
    this.consultaPublicaService.consultarPDF(dscIe)
      .subscribe({
        next: (response) => {
          try {
            const byteCharacters = atob(response.arquivo);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
              byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const blob = new Blob([byteArray], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = response.nomeArquivo;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            this.loading = false;
          } catch (error) {
            console.error('Erro ao processar PDF:', error);
            this.loading = false;
            Swal.fire({
              icon: 'error',
              title: 'Erro',
              text: 'Erro ao processar o arquivo PDF.'
            });
          }
        },
        error: (error) => {
          console.error('Erro ao buscar PDF:', error);
          this.loading = false;
          Swal.fire({
            icon: 'error',
            title: 'Erro',
            text: 'Erro ao gerar o comprovante em PDF.'
          });
        }
      });
  }

  getCnaePrimario(cnaes: any[]): any {
    if (!cnaes || cnaes.length === 0) return null;
    if (cnaes.length === 1) return cnaes[0];
    return cnaes.find(cnae => cnae.primario);
  }

  private formatarDadosIE(response: ConsultaIEResponse): DadosIEFormatado {
    const cpfCnpjFormatado = this.formatarCpfCnpj(response.cpfCnpj) || 'Não informado';
    const cnaes = [];
    
    if (response.cnaePrincipal && response.cnaePrincipal.length > 0) {
      response.cnaePrincipal.forEach(cnaeItem => {
        if (!cnaeItem.datFim) { 
          cnaes.push({
            codigo: cnaeItem.cnae.codCnae || 'Não informado',
            descricao: cnaeItem.cnae.dscCnae || 'Não informado',
            primario: cnaeItem.numPrincipal === 1
          });
        }
      });
    }

    if (response.cnaesSecundarios && response.cnaesSecundarios.length > 0) {
      response.cnaesSecundarios.forEach(cnaeItem => {
        if (!cnaeItem.datFim) { 
          cnaes.push({
            codigo: cnaeItem.cnae.codCnae || 'Não informado',
            descricao: cnaeItem.cnae.dscCnae || 'Não informado',
            primario: false
          });
        }
      });
    }

    const endereco = response.enderecoEstabelecimento?.endereco;
    const enderecoFormatado = {
      logradouro: endereco?.dscLogradouro || 'Não informado',
      numero: endereco?.dscNumero || 'Não informado',
      complemento: endereco?.dscComplemento || 'Não informado',
      cep: this.formatarCep(endereco?.cep?.codCep || '') || 'Não informado',
      bairro: endereco?.dscBairro || endereco?.cep?.dscBairro || 'Não informado',
      municipio: endereco?.cep?.cidade?.dscCidade || 'Não informado',
      estado: endereco?.cep?.cidade?.estado?.codigo || 'Não informado'
    };

    const dataSituacaoIE = response.dataSituacaoInscricaoEstadual
      ? this.formatarData(response.dataSituacaoInscricaoEstadual) 
      : 'Não informado';
    
    const dataSituacaoCpfCnpj = response.dataSituacaoCpfCnpj
      ? this.formatarData(response.dataSituacaoCpfCnpj)
      : 'Não informado';

    return {
      inscricaoEstadual: response.dscInscricaoEstadual || 'Não informado',
      cpfCnpj: cpfCnpjFormatado,
      razaoSocial: response.dscRazaoSocial || 'Não informado',
      categoriaOcupacao: response.dscCategoriaEstabelecimento || 'Não informado',
      situacaoIE: response.situacaoInscricaoEstadual || 'Não informado',
      dataSituacaoIE,
      situacaoCpfCnpj: response.situacaoCpfCnpj || 'Não informado',
      dataSituacaoCpfCnpj,
      cnaes,
      endereco: enderecoFormatado
    };
  }

  private formatarCpfCnpj(valor: string): string {
    if (!valor) return '';
    
    const apenasNumeros = valor.replace(/\D/g, '');
    
    if (apenasNumeros.length === 11) {
      return apenasNumeros.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    } else if (apenasNumeros.length === 14) {
      return apenasNumeros.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
    }
    
    return '';
  }

  private formatarCep(cep: string): string {
    if (!cep) return '';
    const apenasNumeros = cep.replace(/\D/g, '');
    if (apenasNumeros.length === 8) {
      return apenasNumeros.replace(/(\d{2})(\d{3})(\d{3})/, '$1.$2-$3');
    }
    return '';
  }

  private formatarData(data: string): string {
    if (!data) return '';
    const [ano, mes, dia] = data.split('-');
    return `${dia}/${mes}/${ano}`;
  }

  scroll() {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  voltarParaHome() {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    this.router.navigate(['/selecao-representante']);
  }
}
